import { ConsoleRequest, DataProviderType } from '../../utils/request';
import { TargetsBaseDataProvider } from './TargetsBaseDataProvider';
import { ConsoleRequestSettings, GetNodeChildrenRequest, GetNodeChildrenResponse, GetRootRequest, Node } from '../type';
import { ProtectedEntitiesEPS, ProtectedEntitiesContainers, Companies } from '@nirvana/shared/constants';
import { DEFAULT_DATAPROVIDER_CONSOLE_REQUESTS } from '../type/TargetSettings';

export class EPSDataProvider extends TargetsBaseDataProvider {
  public async getRootNode(payload: GetRootRequest, settings: ConsoleRequestSettings): Promise<Node> {
    try {
      const endpoint = settings.EPS?.rootNode?.endpoint ?? DEFAULT_DATAPROVIDER_CONSOLE_REQUESTS.EPS.rootNode.endpoint;
      const method = settings.EPS?.rootNode?.method ?? DEFAULT_DATAPROVIDER_CONSOLE_REQUESTS.EPS.rootNode.method;

      return await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.EPS,
        endpoint: endpoint,
        method: method,
        payload: payload,
      });
    } catch (e) {
      throw new Error(e);
    }
  }

  public async getChildNodes(
    payload: GetNodeChildrenRequest,
    settings: ConsoleRequestSettings
  ): Promise<GetNodeChildrenResponse> {
    try {
      const endpoint =
        settings.EPS?.childNodes?.endpoint ?? DEFAULT_DATAPROVIDER_CONSOLE_REQUESTS.EPS.childNodes.endpoint;
      const method = settings.EPS?.childNodes?.method ?? DEFAULT_DATAPROVIDER_CONSOLE_REQUESTS.EPS.childNodes.method;

      return await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.EPS,
        endpoint: endpoint,
        method: method,
        payload: payload,
      });
    } catch (e) {
      throw new Error(e);
    }
  }

  public getViewType(type: number): number {
    if (!this.dependencies.isCloud()) {
      return ProtectedEntitiesEPS.viewType.EPS_TREE_VIEW;
    }

    if (
      [
        ProtectedEntitiesEPS.ROOT,
        ProtectedEntitiesEPS.ACTIVE_DIRECTORY,
        ProtectedEntitiesEPS.CUSTOM_GROUPS,
        ProtectedEntitiesEPS.TYPE_EXCLUDED_FOLDER,
        ProtectedEntitiesEPS.TYPE_AD_COMPUTER_GROUP,
        ProtectedEntitiesEPS.TYPE_CUSTOM_GROUP,
        ProtectedEntitiesEPS.TYPE_AWS_GROUP,
        ProtectedEntitiesEPS.TYPE_AWS_ACCOUNT,
        ProtectedEntitiesEPS.TYPE_AWS_REGION,
        ProtectedEntitiesEPS.TYPE_AWS_AZ,
        ProtectedEntitiesEPS.TYPE_AWS_INSTANCE,
        ProtectedEntitiesEPS.TERMINATED_INSTANCES_GROUP,
        ProtectedEntitiesContainers.TYPE_COLLECTION,
        ProtectedEntitiesContainers.TYPE_CUSTOM_GROUPS,
        ProtectedEntitiesContainers.TYPE_FOLDER,
        ProtectedEntitiesContainers.TYPE_HOST,
        ProtectedEntitiesEPS.TYPE_VMWARE_COLLECTION,
        ProtectedEntitiesEPS.TYPE_VMWARE_CENTER,
        ProtectedEntitiesEPS.TYPE_VMWARE_DATACENTER,
        ProtectedEntitiesEPS.TYPE_VMWARE_FOLDER,
        ProtectedEntitiesEPS.TYPE_VMWARE_COMPUTE_RESOURCE,
        ProtectedEntitiesEPS.TYPE_VMWARE_CLUSTER_COMPUTE_RESOURCE,
        ProtectedEntitiesEPS.TYPE_VMWARE_RESOURCE_POOL,
        ProtectedEntitiesEPS.TYPE_VMWARE_VIRTUAL_APP,
        ProtectedEntitiesEPS.TYPE_VMWARE_HOST_SYSTEM,
        ProtectedEntitiesEPS.TYPE_VMWARE_VIRTUAL_MACHINE,
      ].includes(type)
    ) {
      return ProtectedEntitiesEPS.viewType.EPS_TREE_VIEW;
    }

    return Companies.viewType.COMPANIES_VIEW;
  }

  public isLeafNode(node: Node, userMode: boolean, showOnlyCompanies: boolean, rootNodeId: string): boolean {
    if (userMode) {
      if (node.type === ProtectedEntitiesContainers.TYPE_CUSTOM_GROUPS) {
        return true;
      }
    }

    const currentNodeIsRoot = node._id === rootNodeId;
    if (!currentNodeIsRoot && node.type === Companies.TYPE_COMPANY && !node.specifics?.canBeManagedByAbove) {
      return true;
    }

    if (userMode && !showOnlyCompanies) {
      if (currentNodeIsRoot) {
        return false;
      } else {
        if (node.type === Companies.TYPE_COMPANY && node.specifics?.rights && !node.specifics.rights.manageCompanies) {
          return true;
        }

        const parentNodeIdIsRoot = node?.parentId === rootNodeId;
        if (node.type === Companies.TYPE_COMPANY_NETWORK && !parentNodeIdIsRoot) {
          return true;
        }
      }
    }
    return false;
  }
}
